/* ------------------------------------------------------------------
  File: Loader.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable loading indicator component for the Accend Holdings website.
//   - Supports spinner, dots, and bar variants.
//   - Supports size options, labels, centered layout, full-page loading,
//     inline loading, and dark-surface compatibility.
//   - Uses the acc-loader namespace to avoid conflicts with Bootstrap.
//   - Suitable for page loading, section loading, form submission,
//     search loading, and dynamic content loading.

/**
 * Loader
 *
 * A reusable loading indicator component.
 *
 * @param {"spinner" | "dots" | "bar"} [variant="spinner"]
 * Controls the visual loading style.
 *
 * @param {"sm" | "md" | "lg"} [size="md"]
 * Controls the loader size.
 *
 * @param {string} [label="Loading"]
 * Accessible and optional visible label.
 *
 * @param {boolean} [showLabel=false]
 * Controls whether the label is visually displayed.
 *
 * @param {boolean} [centered=false]
 * Centers the loader inside its parent container.
 *
 * @param {boolean} [fullPage=false]
 * Makes the loader fill the viewport.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import "./Loader.css";

const Loader = ({
  variant = "spinner",
  size = "md",
  label = "Loading",
  showLabel = false,
  centered = false,
  fullPage = false,
  className = "",
  ...props
}) => {
  const loaderClasses = [
    "acc-loader",
    `acc-loader--${variant}`,
    `acc-loader--${size}`,
    centered ? "acc-loader--centered" : "",
    fullPage ? "acc-loader--full-page" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={loaderClasses}
      role="status"
      aria-live="polite"
      aria-label={!showLabel ? label : undefined}
      {...props}
    >
      {variant === "spinner" && <span className="acc-loader__spinner" />}

      {variant === "dots" && (
        <span className="acc-loader__dots" aria-hidden="true">
          <span className="acc-loader__dot" />
          <span className="acc-loader__dot" />
          <span className="acc-loader__dot" />
        </span>
      )}

      {variant === "bar" && (
        <span className="acc-loader__bar" aria-hidden="true">
          <span className="acc-loader__bar-fill" />
        </span>
      )}

      {showLabel && <span className="acc-loader__label">{label}</span>}
    </div>
  );
};

export default Loader;